import { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  BookOpen,
  ExternalLink,
  FileText,
  FlaskConical,
  Loader2,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import { functions, db } from '../../lib/firebase';

interface ArxivPaper {
  id: string;
  title: string;
  abstract: string;
  authors: string[];
  field: string;
  publishedAt: string;
  score: number;
  url: string;
  isOpenAccess: boolean;
}

interface FetchResult {
  success: boolean;
  totalFetched: number;
  afterDedup: number;
  savedCount: number;   // set client-side after Firestore write
  firestoreOk: boolean; // set client-side after Firestore write
  papers: ArxivPaper[];
}

const FIELD_COLORS: Record<string, string> = {
  'AI / ML':      'bg-violet-500/15 text-violet-400 ring-violet-500/25',
  'Physics':      'bg-blue-500/15 text-blue-400 ring-blue-500/25',
  'Mathematics':  'bg-amber-500/15 text-amber-400 ring-amber-500/25',
  'Biology':      'bg-emerald-500/15 text-emerald-400 ring-emerald-500/25',
  'Neuroscience': 'bg-pink-500/15 text-pink-400 ring-pink-500/25',
  'Economics':    'bg-orange-500/15 text-orange-400 ring-orange-500/25',
  'Statistics':   'bg-cyan-500/15 text-cyan-400 ring-cyan-500/25',
};
function fieldBadge(field: string) {
  return FIELD_COLORS[field] ?? 'bg-surface-raised text-fg-muted ring-border';
}

function pdfUrl(abstractUrl: string) {
  return abstractUrl.replace('arxiv.org/abs/', 'arxiv.org/pdf/');
}

function ScoreBar({ score }: { score: number }) {
  const pct = Math.min(100, Math.round(score));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-raised">
        <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-9 shrink-0 text-right text-xs tabular-nums text-fg-subtle">{score.toFixed(0)}</span>
    </div>
  );
}

function FeaturedCard({ paper, rank }: { paper: ArxivPaper; rank: number }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <article className="erudis-panel group relative flex flex-col gap-3 p-5 transition-shadow hover:shadow-md">
      <div className="absolute -left-3 top-5 flex h-6 w-6 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white shadow">
        {rank}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${fieldBadge(paper.field)}`}>
          <FlaskConical size={10} strokeWidth={2} />{paper.field}
        </span>
        {paper.isOpenAccess && (
          <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-400 ring-1 ring-emerald-500/20">
            Open Access
          </span>
        )}
        <span className="ml-auto text-[11px] tabular-nums text-fg-subtle">{paper.publishedAt}</span>
      </div>
      <a href={paper.url} target="_blank" rel="noopener noreferrer"
        className="flex items-start gap-1 font-semibold leading-snug text-fg transition-colors hover:text-brand">
        <span className="flex-1">{paper.title}</span>
        <ExternalLink size={14} strokeWidth={1.5} className="mt-0.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70" />
      </a>
      {paper.authors.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-fg-muted">
          <Users size={12} strokeWidth={1.5} className="shrink-0" />
          <span className="truncate">
            {paper.authors.slice(0, 4).join(', ')}{paper.authors.length > 4 ? ` +${paper.authors.length - 4} more` : ''}
          </span>
        </div>
      )}
      {paper.abstract && (
        <div>
          <p className={`text-xs leading-relaxed text-fg-muted ${expanded ? '' : 'line-clamp-3'}`}>{paper.abstract}</p>
          {paper.abstract.length > 200 && (
            <button onClick={() => setExpanded(e => !e)} className="mt-1 text-[11px] text-brand hover:underline">
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}
      <div>
        <p className="mb-1 text-[10px] font-medium uppercase tracking-widest text-fg-subtle">Relevance</p>
        <ScoreBar score={paper.score} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <BookOpen size={11} strokeWidth={1.5} className="text-fg-subtle" />
          <span className="font-mono text-[10px] text-fg-subtle">{paper.id}</span>
        </div>
        <a
          href={pdfUrl(paper.url)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 rounded-md border border-border bg-surface-raised px-2.5 py-1 text-[11px] font-medium text-fg-muted transition hover:border-brand/40 hover:text-brand"
        >
          <FileText size={12} strokeWidth={1.5} />
          PDF
        </a>
      </div>
    </article>
  );
}

function CompactRow({ paper }: { paper: ArxivPaper }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="rounded-lg border border-border bg-surface-card p-4 transition hover:border-brand/30">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${fieldBadge(paper.field)}`}>
              {paper.field}
            </span>
            <span className="text-[11px] tabular-nums text-fg-subtle">{paper.publishedAt}</span>
          </div>
          <a href={paper.url} target="_blank" rel="noopener noreferrer"
            className="block text-sm font-medium leading-snug text-fg hover:text-brand">
            {paper.title}
          </a>
          {paper.authors.length > 0 && (
            <p className="text-[11px] text-fg-muted">
              {paper.authors.slice(0, 3).join(', ')}{paper.authors.length > 3 ? ` +${paper.authors.length - 3}` : ''}
            </p>
          )}
          {paper.abstract && (
            <div>
              <p className={`text-xs leading-relaxed text-fg-subtle ${expanded ? '' : 'line-clamp-2'}`}>{paper.abstract}</p>
              {paper.abstract.length > 150 && (
                <button onClick={() => setExpanded(e => !e)} className="mt-0.5 text-[11px] text-brand hover:underline">
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="text-right">
            <span className="text-xs font-semibold tabular-nums text-brand">{paper.score.toFixed(0)}</span>
            <p className="text-[9px] text-fg-subtle">score</p>
          </div>
          <a
            href={pdfUrl(paper.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 rounded border border-border bg-surface-raised px-2 py-0.5 text-[10px] font-medium text-fg-muted transition hover:border-brand/40 hover:text-brand"
          >
            <FileText size={10} strokeWidth={1.5} />
            PDF
          </a>
        </div>
      </div>
    </li>
  );
}

const isDev = process.env.NODE_ENV === 'development';

export function ArxivTestPage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FetchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  async function handleFetch() {
    if (!functions) {
      setError('Firebase Functions not initialised — check your .env config.');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable<Record<string, never>, Omit<FetchResult, 'savedCount' | 'firestoreOk'>>(functions, 'testArxivFetch');
      const res = await fn({});
      const data: FetchResult = { ...res.data, savedCount: 0, firestoreOk: false };

      // Save papers to production Firestore from the browser
      if (db && data.papers.length > 0) {
        try {
          await Promise.all(
            data.papers.map(p =>
              setDoc(
                doc(db!, 'newsPapers', p.id),
                {
                  id: p.id,
                  title: p.title,
                  abstract: p.abstract,
                  authors: p.authors,
                  source: 'arxiv',
                  url: p.url,
                  publishedAt: p.publishedAt,
                  researchArea: p.field,
                  isOpenAccess: p.isOpenAccess,
                  score: p.score,
                  citationCount: 0,
                  doi: null,
                  aiSummary: null,
                  isSummarized: false,
                  resonateCount: 0,
                  likeCount: 0,
                  viewCount: 0,
                  commentCount: 0,
                  fetchedAt: serverTimestamp(),
                  summarizedAt: null,
                },
                { merge: true }
              )
            )
          );
          data.firestoreOk = true;
          data.savedCount = data.papers.length;
        } catch (fsErr: any) {
          console.warn('[ArxivTestPage] Firestore save failed:', fsErr.message);
        }
      }

      setResult(data);
    } catch (err: any) {
      const code: string = err?.code ?? '';
      const msg: string = err?.message ?? 'Unknown error';
      if (isDev && (msg === 'internal' || code === 'functions/internal')) {
        setError('Could not reach the Functions emulator. Run: firebase emulators:start --only functions');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  const top5 = useMemo(() => result?.papers.slice(0, 5) ?? [], [result?.papers]);
  const rest = useMemo(() => result?.papers.slice(5) ?? [], [result?.papers]);

  const filteredRest = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return rest;
    return rest.filter(p =>
      p.title.toLowerCase().includes(q) ||
      p.authors.some(a => a.toLowerCase().includes(q)) ||
      p.abstract.toLowerCase().includes(q) ||
      p.field.toLowerCase().includes(q)
    );
  }, [rest, search]);

  return (
    <div className="mx-auto max-w-2xl space-y-8 pb-12">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-fg">arXiv Feed Test</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Fetch the latest papers from arXiv, save them to Firestore, and preview the pipeline output.
          </p>
        </div>
        <button
          onClick={handleFetch}
          disabled={loading}
          className="flex shrink-0 items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {loading ? <Loader2 size={15} strokeWidth={2} className="animate-spin" /> : <RefreshCw size={15} strokeWidth={2} />}
          {loading ? 'Fetching…' : result ? 'Refresh' : 'Fetch Papers'}
        </button>
      </div>

      {isDev && (
        <div className="rounded-lg border border-brand/25 bg-brand/5 px-4 py-3 font-mono text-[11px] text-fg-muted">
          <span className="font-semibold text-brand">DEV</span> — Functions route to emulator:5001.
          Keep <code className="text-fg">firebase emulators:start --only functions</code> running.
        </div>
      )}

      <p className="text-[11px] italic text-fg-subtle">
        "Thank you to arXiv for use of its open access interoperability."
      </p>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="erudis-panel animate-pulse space-y-3 p-5">
              <div className="flex gap-2">
                <div className="h-5 w-16 rounded-full bg-surface-raised" />
                <div className="h-5 w-20 rounded-full bg-surface-raised" />
                <div className="ml-auto h-5 w-24 rounded-full bg-surface-raised" />
              </div>
              <div className="h-5 w-3/4 rounded bg-surface-raised" />
              <div className="h-4 w-1/2 rounded bg-surface-raised" />
              <div className="h-2 w-full rounded-full bg-surface-raised" />
            </div>
          ))}
        </div>
      )}

      {result && !loading && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total fetched', value: result.totalFetched },
              { label: 'After dedup', value: result.afterDedup },
              { label: 'Saved to DB', value: result.savedCount },
              { label: 'Showing', value: result.papers.length },
            ].map(s => (
              <div key={s.label} className="erudis-panel p-3 text-center">
                <p className="text-xl font-bold tabular-nums text-brand">{s.value}</p>
                <p className="mt-0.5 text-[10px] text-fg-muted">{s.label}</p>
              </div>
            ))}
          </div>

          {result.firestoreOk ? (
            <p className="rounded-lg border border-emerald-500/25 bg-emerald-500/8 px-3 py-2 text-[11px] text-emerald-400">
              Papers saved to Firestore. View them in <strong>Discover → Papers</strong> tab.
            </p>
          ) : (
            <p className="rounded-lg border border-amber-500/25 bg-amber-500/8 px-3 py-2 text-[11px] text-amber-400">
              Firestore save failed — papers shown below but not persisted. Run{' '}
              <code className="font-mono">firebase emulators:start --only functions,firestore</code> to enable saves.
            </p>
          )}

          {/* Top 5 */}
          <section>
            <h2 className="mb-4 font-display text-lg font-semibold text-fg">Top 5 Papers</h2>
            <div className="space-y-4 pl-4">
              {top5.map((p, i) => <FeaturedCard key={p.id} paper={p} rank={i + 1} />)}
            </div>
          </section>

          {/* All remaining papers */}
          {rest.length > 0 && (
            <section>
              <div className="mb-4 flex items-center justify-between gap-4">
                <h2 className="font-display text-lg font-semibold text-fg">
                  All Papers
                  <span className="ml-2 text-sm font-normal text-fg-muted">
                    {filteredRest.length}{search ? ` of ${rest.length}` : ''} remaining
                  </span>
                </h2>
              </div>
              <div className="relative mb-4">
                <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
                <input
                  type="text"
                  placeholder="Search by title, author, institution, field…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface-card py-2 pl-9 pr-4 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand/40"
                />
              </div>
              {filteredRest.length === 0 ? (
                <p className="text-sm text-fg-muted">No papers match "{search}".</p>
              ) : (
                <ul className="space-y-2">
                  {filteredRest.map(p => <CompactRow key={p.id} paper={p} />)}
                </ul>
              )}
            </section>
          )}
        </>
      )}

      {!loading && !result && !error && (
        <div className="erudis-panel flex flex-col items-center gap-3 py-16 text-center">
          <BookOpen size={32} strokeWidth={1} className="text-fg-subtle" />
          <p className="text-sm text-fg-muted">
            Press <strong>Fetch Papers</strong> to pull the latest arXiv papers and save them to Firestore.
          </p>
        </div>
      )}
    </div>
  );
}
